import { useState, useEffect, useRef } from 'react';
import { Activity } from 'lucide-react';
import Button from '../components/Button';
import Header from '../components/Header';
import ProfileMenu from '../components/ProfileMenu';
import { useApp } from '../lib/AppContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { useProfileData } from '../hooks/useProfileData';
import WeeklyPracticeTracker from '../components/WeeklyPracticeTracker';
import MonthlyPracticeSection from '../components/MonthlyPracticeSection';
import LifetimeInsightsCard from '../components/LifetimeInsightsCard';
import WeeklyEcho from '../components/WeeklyEcho';
import CivicLevelCard from '../components/CivicLevelCard';
import BadgesModal from '../components/BadgesModal';
import type { CivicLevel } from '../lib/civicLevelTypes';

// One repeating tile of the EKG wave, 32 units wide, 20 units tall, midline at y=10
const TILE_W = 32;
const EKG_POINTS = `0,10 5,10 7,10 9,3 11,17 13,6 14,10 19,10 21,10 23,3 25,17 27,6 28,10 32,10`;

function HeartbeatIcon() {
  const groupRef = useRef<SVGGElement>(null);
  const offsetRef = useRef(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const SPEED = 7; // px per second
    let last = performance.now();

    function tick(now: number) {
      const dt = (now - last) / 1000;
      last = now;
      offsetRef.current = (offsetRef.current + SPEED * dt) % TILE_W;
      if (groupRef.current) {
        groupRef.current.style.transform = `translateX(${-offsetRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Render 3 tiles so there's always full coverage while scrolling
  const tiles = [0, TILE_W, TILE_W * 2];

  return (
    <div className="w-12 h-12 bg-[#40E0D0]/10 rounded-full flex items-center justify-center overflow-hidden">
      <svg width="32" height="20" viewBox="0 0 32 20" fill="none" style={{ overflow: 'visible' }}>
        <defs>
          <clipPath id="hb-rhy-clip">
            <rect x="0" y="0" width="32" height="20" />
          </clipPath>
        </defs>
        <g clipPath="url(#hb-rhy-clip)">
          <g ref={groupRef} style={{ willChange: 'transform' }}>
            {tiles.map(offset => (
              <polyline
                key={offset}
                points={EKG_POINTS.split(' ').map(p => {
                  const [x, y] = p.split(',');
                  return `${parseFloat(x) + offset},${y}`;
                }).join(' ')}
                stroke="#40E0D0"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            ))}
          </g>
        </g>
      </svg>
    </div>
  );
}

export default function ProfileComplete() {
  const { navigateTo } = useApp();
  const { user, profile: effectiveProfile, isLoggedOut, isPreviewMode } = useEffectiveUser();
  const [refreshKey, setRefreshKey] = useState(0);
  const [showBadgesModal, setShowBadgesModal] = useState(false);

  // Force refresh profile data each time this screen is mounted/viewed
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const profileData = useProfileData(user?.id || null, refreshKey);

  // Use effective profile in preview mode, otherwise use fetched data
  const displayProfile = isPreviewMode && effectiveProfile ? effectiveProfile : profileData;

  // GUEST VIEW
  if (isLoggedOut || !user) {
    return (
      <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
        <Header
          mode="title"
          title="Profile"
          rightContent={
            <ProfileMenu onSettingsClick={() => navigateTo('settings')} />
          }
        />

        <div className="max-w-2xl mx-auto px-6 py-6 space-y-6">
          {/* Guest Hero */}
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#181818] rounded-3xl p-8 border text-center" style={{ borderColor: 'rgba(64, 224, 208, 0.55)', boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-[#40E0D0]/10 rounded-full flex items-center justify-center">
                <Activity className="w-6 h-6 text-[#40E0D0]" strokeWidth={2.25} />
              </div>
            </div>
            <h2 className="text-[20px] font-medium text-[#F5F5F5] leading-relaxed mb-2">
              You're just getting started on your civic journey.
            </h2>
            <p className="text-[13px] text-[#A0A0A0] mb-6" style={{ lineHeight: '1.5' }}>
              Create a profile to track your practice, discover patterns, and deepen your engagement.
            </p>
            <div className="flex flex-row gap-3 justify-center">
              <Button variant="primary" onClick={() => navigateTo('auth', undefined, { authMode: 'signup' })}>
                Create account
              </Button>
              <Button variant="secondary" onClick={() => navigateTo('auth', undefined, { authMode: 'signin' })}>
                Sign in
              </Button>
            </div>
          </div>

          {/* Placeholder Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {["Today's beat", "Stories you've tuned into", 'Emotional notes captured'].map((label) => (
              <div
                key={label}
                className="bg-[#181818]/40 rounded-2xl p-5 border border-[#282828]/50 flex flex-col items-center justify-center min-h-[110px] opacity-60"
              >
                <div className="text-[32px] font-bold text-[#52E3C2]/40 mb-1">—</div>
                <div className="text-[13px] text-[#808080] text-center font-medium">{label}</div>
              </div>
            ))}
          </div>

          {/* Disabled Weekly Tracker */}
          <div className="bg-[#181818]/40 rounded-2xl p-5 border border-[#282828]/50 opacity-60">
            <h3 className="text-[13px] font-semibold text-[#F5F5F5] mb-3">
              This week
            </h3>
            <div className="grid grid-cols-7 gap-2">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className="text-[11px] text-[#808080] font-medium">{day}</div>
                  <div className="w-full aspect-square rounded-lg border border-[#282828] bg-transparent" />
                </div>
              ))}
            </div>
          </div>

          {/* Disabled Monthly Calendar */}
          <div className="bg-[#181818]/40 rounded-2xl p-5 border border-[#282828]/50 opacity-60">
            <h3 className="text-[13px] font-semibold text-[#F5F5F5] mb-3">
              Your Civic Practice (This Month)
            </h3>
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: 35 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded border border-[#282828] bg-transparent text-[10px] text-[#606060] flex items-center justify-center"
                >
                  {i < 31 ? i + 1 : ''}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LOADING STATE (skip in preview mode)
  if (!isPreviewMode && profileData.isLoading) {
    return (
      <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
        <Header
          mode="title"
          title="Profile"
          rightContent={
            <ProfileMenu onSettingsClick={() => navigateTo('settings')} />
          }
        />
        <div className="flex items-center justify-center py-20">
          <p className="text-[#A0A0A0]">Loading your civic journey...</p>
        </div>
      </div>
    );
  }

  // ERROR STATE (skip in preview mode)
  if (!isPreviewMode && profileData.error) {
    return (
      <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
        <Header
          mode="title"
          title="Profile"
          rightContent={
            <ProfileMenu onSettingsClick={() => navigateTo('settings')} />
          }
        />
        <div className="flex items-center justify-center py-20">
          <p className="text-[#A0A0A0]">We couldn't load your profile. Try again.</p>
        </div>
      </div>
    );
  }

  // LOGGED-IN VIEW
  return (
    <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <Header
        mode="title"
        title="Profile"
        rightContent={
          <ProfileMenu onSettingsClick={() => navigateTo('settings')} />
        }
      />

      <div className="max-w-2xl mx-auto px-6 py-6 space-y-6">
        {/* 1. YOUR CIVIC RHYTHM (AI Identity Story) */}
        <div className="bg-[#181818] rounded-3xl p-6 sm:p-8 border" style={{ borderColor: 'rgba(64, 224, 208, 0.55)', boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
          <div className="flex justify-center mb-4">
            <HeartbeatIcon />
          </div>
          <h3 className="text-[14px] font-semibold text-[#40E0D0] text-center mb-2">
            Your Civic Rhythm
          </h3>
          <p className="text-[16px] sm:text-[17px] font-medium text-[#F5F5F5] leading-relaxed text-center" style={{ lineHeight: '1.6' }}>
            {displayProfile.heroInsight?.text || 'Your civic journey is just beginning.'}
          </p>
        </div>

        {/* 2. YOUR CIVIC LEVEL */}
        <CivicLevelCard
          civicLevel={(displayProfile.civicLevel || displayProfile.civic_level || 'Observer') as CivicLevel}
          xp={displayProfile.xp || 0}
          userId={user.id}
          onViewBadges={() => setShowBadgesModal(true)}
        />

        {/* 3. YOUR WEEKLY THEME */}
        {displayProfile.weeklyEcho?.show && (
          <WeeklyEcho text={displayProfile.weeklyEcho.text} />
        )}

        {/* 4. THIS WEEK (7-day streak) */}
        <WeeklyPracticeTracker
          activeDays={displayProfile.practiceGrid
            ? displayProfile.practiceGrid.filter(day => day.hasActivity).map(day => day.date)
            : []}
        />

        {/* 5. YOUR CIVIC PRACTICE (THIS MONTH) */}
        <MonthlyPracticeSection
          activeDays={displayProfile.practiceGrid
            ? displayProfile.practiceGrid.filter(day => day.hasActivity).map(day => day.date)
            : []}
        />

        {/* 6. EMOTIONAL PULSE (THIS MONTH) */}
        <div className="bg-[#181818] rounded-2xl p-6 border border-[#282828]" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
          <h3 className="text-[14px] font-semibold text-[#F5F5F5] mb-2">
            Emotional Pulse (This Month)
          </h3>
          <p className="text-[12px] text-[#CCCCCC] mb-1" style={{ lineHeight: '1.5' }}>
            {displayProfile.emotionalPulseMonth?.insightText || 'Your month in feelings.'}
          </p>

          {(() => {
            const emotionalPulse = displayProfile.emotionalPulseMonth || { moved: 0, empowered: 0, curious: 0, heavy: 0 };
            const totalEmotionalReactions = Object.values(emotionalPulse).reduce(
              (acc: number, val: any) => (typeof val === 'number' ? acc + val : acc),
              0
            );

            return (
              <>
                <p className="text-[11px] text-[#888888] mb-4">
                  {totalEmotionalReactions} emotional reaction{totalEmotionalReactions !== 1 ? 's' : ''} this month
                </p>

                <div className="space-y-3">
                  {[
                    { label: 'Moved', count: emotionalPulse.moved || 0, color: '#FF79C6' },
                    { label: 'Empowered', count: emotionalPulse.empowered || 0, color: '#52E3C2' },
                    { label: 'Curious', count: emotionalPulse.curious || 0, color: '#FFB86C' },
                    { label: 'Heavy', count: emotionalPulse.heavy || 0, color: '#BD93F9' },
                  ].map(({ label, count, color }) => {
                    const maxCount = Math.max(
                      emotionalPulse.moved || 0,
                      emotionalPulse.empowered || 0,
                      emotionalPulse.curious || 0,
                      emotionalPulse.heavy || 0
                    );
                    const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;

                    return (
                      <div key={label}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-[13px] text-[#F5F5F5]">{label}</span>
                          </div>
                          <span className="text-[12px] text-[#A0A0A0]">{count}</span>
                        </div>
                        <div className="h-2 bg-[#222222] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: color,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>

        {/* 7. LIFETIME CIVIC INSIGHTS (COLLAPSIBLE) */}
        <LifetimeInsightsCard
          storiesTunedInto={displayProfile.storiesTunedInto || 0}
          emotionalNotesCaptured={displayProfile.emotionalNotesCaptured || 0}
          civicIdentityLifetime={displayProfile.civicIdentityLifetime || []}
        />

      </div>

      <BadgesModal
        isOpen={showBadgesModal}
        onClose={() => setShowBadgesModal(false)}
        userId={user.id}
        currentXP={displayProfile.xp || 0}
      />
    </div>
  );
}
